import styles from "./index.module.css";
import "./index.css";

import React from 'react';
import DataComponent, {executeComponentCallback} from 'Core/components/DataComponent';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {getGlobalActions} from 'Core/helpers/redux';
import * as actions from './actions';
import Button, {BUTTON_DISPLAY_TYPE, BUTTON_STYLE} from 'Core/components/display/Button';
import {icon_font_create_symbol} from 'Config/app';
import {getArray, getBool, getObject, getString, isset, trimArray} from 'Core/helpers/data';
import ConditionList, {
	CONDITION_LIST_FIELD_DISPLAY_TYPE, CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE,
	CONDITION_LIST_VALUE_CLEAR_TYPE
} from 'Core/components/advanced/ConditionList';
import ConfirmDialog from 'Core/components/dialogs/ConfirmDialog';
import CustomerSegmentPreviewDialog from 'Components/dialogs/CustomerSegmentPreviewDialog';
import DataValueValidation, {ValidationRuleObject} from 'Core/validation';
import {cloneDeep, get, isEmpty, isEqual, set} from 'lodash';
import {selectors} from "Core/store/reducers";
import {waitingFunction} from 'Core/helpers/function';
import {hideLoading, showLoading} from 'Core/helpers/loading';
import SelectCustomerSegmentDialog from 'Components/dialogs/SelectCustomerSegmentDialog';
import {COMPARATOR} from 'Core/const/global';
import Label from 'Core/components/display/Label';
import {INSERT_VALUE_BUTTON_TYPE} from 'Core/components/advanced/InsertValueButton/const';
import {InsertValueDialogSectionDataObject} from 'Core/components/dialogs/InsertValueDialog/dataObjects';
import RelativeDateSection from 'Components/dialogs/InsertValueDialog/sections/RelativeDateSection';
import {stringContainsTag} from 'Tags/helpers';

/**
 * Redux 'mapStateToProps' function
 *
 * @param {object} state - Redux entire store state.
 * @return {Object<string, any>} Mapped props that can be used in a component.
 */
const mapStateToProps = state => ({
	appLocale: selectors.i18n.getLocale(state)
});


class ImportCustomersFromSegment extends DataComponent {
	constructor(props) {
		super(props, {
			/** @type {ConditionListItemDataObject[]} */
			data: cloneDeep(getArray(props, 'defaultData')),
			/**
			 * Flag that specifies if data is being updated
			 * @type {boolean}
			 */
			updatingData: false,

			/** 
			 * Condition field options
			 * @type {ConditionListFieldItemDataObject[]} 
			 */
			fieldOptions: [],
			/**
			 * Flag that specifies if condition field options are being loaded
			 * @type {boolean}
			 */
			fieldOptionsLoading: false,

			/**
			 * Flag that specifies if component is collapsed (input is not visible)
			 * @note This is relevant only if 'collapsable' prop is set to true.
			 * @type {boolean}
			 */
			collapsed: getBool(props, 'defaultCollapsed'),
		}, {
			translationPath: 'ImportCustomersFromSegment',
			domPrefix: 'import-customers-from-segment',
			disableLoad: true,
			optimizedUpdate: true,
			optimizedUpdateIgnoreProps: ['onSuccess', 'onError', 'onEnterKey', 'onChange'],
		});
		
		// Dialog methods
		this.openImportSegmentDialog = this.openImportSegmentDialog.bind(this);
		this.openPreviewDialog = this.openPreviewDialog.bind(this);
		
		// Action methods
		this.submitSegment = this.submitSegment.bind(this);
		this.handleSubmitSegmentButtonClick = this.handleSubmitSegmentButtonClick.bind(this);
		
		// Render methods
		this.renderSearch = this.renderSearch.bind(this);
		this.renderActions = this.renderActions.bind(this);
		this.renderContent = this.renderContent.bind(this);
	}

	async asyncComponentDidMount(override = false) {
		await super.asyncComponentDidMount(override);
		
		const {fetchCustomersSegmentColumnsAction} = this.props;
		
		// Load field options
		await this.setState({fieldOptionsLoading: true})
			.then(() => this.executeAbortableActionMount(fetchCustomersSegmentColumnsAction))
			.then(fieldOptions => this.setState({fieldOptions: fieldOptions, fieldOptionsLoading: false}));
	}
	
	componentDidUpdate(prevProps, prevState, snapshot) {
		return super.componentDidUpdate(prevProps, prevState, snapshot)
			// Update field options when the language changes to get the proper labels
			.then(async () => {
				if (!isEqual(this.props.appLocale, prevProps.appLocale)) {
					const {fetchCustomersSegmentColumnsAction} = this.props;
					await this.setState({fieldOptionsLoading: true})
						.then(() => this.executeAbortableAction(fetchCustomersSegmentColumnsAction))
						.then(fieldOptions => this.setState({fieldOptions: fieldOptions, fieldOptionsLoading: false}));
				}
				return this.state;
			});
	}


	// Component property methods ---------------------------------------------------------------------------------------
	/**
	 * Get component's ID that can be used as a DOM element id attribute value
	 * @return {string}
	 */
	getDomId() { return this.getOption('domPrefix'); }


	// Data methods -----------------------------------------------------------------------------------------------------
	/**
	 * Set component's main data in local state if it is different from current component's main data
	 * @note This method will not mutate the passed data.
	 *
	 * @param {any} data - Data to set. If data is the same as current local component's state data, it will not be set.
	 * @return {Promise<object>} Promise that is resolved with entire component's local state after it has been updated.
	 */
	setData(data) {
		const currentData = this.getData();
		const dataToSet = cloneDeep(data);

		// Set data if it is different from current data
		if(!isEqual(currentData, dataToSet)) {
			return this.setState({updatingData: true})
				.then(() => this.setState({data: dataToSet}))
				.then(() => this.setState({updatingData: false}));
		}
		// Do not set data if it is not different from current data
		else {
			return new Promise(resolve => resolve(this.state));
		}
	}
	
	
	// Validation methods -----------------------------------------------------------------------------------------------
	/** @inheritDoc */
	validate() {
		const dataToValidate = this.getData();
		const dataValidation = new DataValueValidation();
		
		dataValidation.addRule('field', new ValidationRuleObject(
			'custom', 
			{validate: (value, options, data, row) => {
				const field = get(row, 'field');
				
				if (!field) return {passed: false, error: 'required'};
				
				if (typeof field === 'string') return {passed: true};
				
				if (!!get(field, 'value')) return {passed: true};
				else return {passed: false, error: 'required'};
			}}
		));

		dataValidation.addTranslation('betweenCount', this.t('minCustom', 'validation.constraint', '', {constraint: 2}));
		dataValidation.addRule('value', new ValidationRuleObject(
			'custom',
			{validate: (value, options, data, row) => {
				if (getString(row, 'comparator') === COMPARATOR.BTW) {
					return (
						getString(row, 'value').split(',').length === 2 ?
							{passed: true} :
							{passed: false, error: 'betweenCount'}
					)
				}
				return {passed: true};
			}}
		));

		// Run validation
		const validationErrors = dataValidation.runOnArray(dataToValidate, 'GUIID');
		if (validationErrors) this.setValidationErrors('', validationErrors).then();
		else this.clearValidationErrors().then();
		return !validationErrors;
	}
	

	// Dialog methods ---------------------------------------------------------------------------------------------------
	/**
	 * Open a dialog to select and apply a customer segment
	 */
	openImportSegmentDialog() {
		const {projectId, openDialogAction, closeDialogAction, addErrorMessageAction} = this.props;

		const dialogGUIID = openDialogAction('', SelectCustomerSegmentDialog, {
			projectId,
			title: this.t('browse_dialog_title'),
			description: this.t('browse_dialog_description'),
			notice: (getArray(this.getData()).length > 0 ? this.t('browse_dialog_notice') : ''),
			saveBtnIcon: 'download',
			saveBtnLabel: this.t('browse_dialog_save_btn'),
			onSave:
				/** @param {CustomerSegmentsItemDataObject|undefined} segment */
				segment => {
					if (isset(segment)) this.setData(segment.conditions).then(() => closeDialogAction(dialogGUIID));
					else addErrorMessageAction(this.t('browse_dialog_save_error'))
				},
		}, {
			id: 'select-customer-segment-dialog',
			className: 'bordered-title',
			closeOnEscape: true,
			closeOnClickOutside: false,
			hideCloseBtn: false,
			maxWidth: 650
		});
		this.setOption(
			'dialogsToCloseOnUnmount',
			trimArray([...this.getOption('dialogsToCloseOnUnmount'), dialogGUIID], 'left')
		);
	}
	
	/**
	 * Open a dialog to preview the customer segment
	 */
	openPreviewDialog() {
		const {
			projectId, successMessage, openDialogAction, closeDialogAction, addSuccessMessageAction, 
			addDefaultMessageAction, collapsable, collapseAfterSubmit, showSubmitButton, 
		} = this.props;
		const {collapsed} = this.state;

		// Validate segment conditions
		if (!this.validate()) return;
		
		const dialogGUIID = openDialogAction('', CustomerSegmentPreviewDialog, {
			projectId,
			showSubmitButton,
			conditions: this.getData(),
			onSave: customers => {
				if (Array.isArray(customers) && customers.length > 0) {
					return this.submitSegment('#customer-segment-preview-dialog')
						.then(res => {
							if (res) {
								if (successMessage) addSuccessMessageAction(successMessage);

								// Clear conditions
								this.setData([]).then();

								if (collapseAfterSubmit && (collapsable && !collapsed)) this.setState({collapsed: true}).then();
								executeComponentCallback(this.props.onSuccess);
								closeDialogAction(dialogGUIID);
							} else {
								executeComponentCallback(this.props.onError);
							}
						});
				} else {
					addDefaultMessageAction(this.t('nothing_to_add'))
					closeDialogAction(dialogGUIID);	
				}
			},
		}, {
			id: 'customer-segment-preview-dialog',
			closeOnEscape: true,
			closeOnClickOutside: true,
			hideCloseBtn: false,
			maxWidth: 1280
		});
		this.setOption(
			'dialogsToCloseOnUnmount',
			trimArray([...this.getOption('dialogsToCloseOnUnmount'), dialogGUIID], 'left')
		);
	}
	
	
	// Action methods ---------------------------------------------------------------------------------------------------
	/**
	 * Submit a segment of customers
	 *
	 * @param {string} [loadingSelector]
	 * @param {ConditionListItemDataObject[]} [conditions]
	 * @return {Promise<*>}
	 */
	submitSegment(loadingSelector, conditions) {
		const {messageDefinitionId, submitCustomersSegmentAction, submitAction, submitId,} = this.props;
		const action = (!!submitAction ? submitAction : submitCustomersSegmentAction);
		const id = (!!submitAction ? submitId : messageDefinitionId);
		
		const dataToSubmit = (isset(conditions) ? conditions : this.getDataToReturn());
		return this.executeAbortableAction(action, dataToSubmit, id, loadingSelector);
	}
	
	/**
	 * Handle button click event for the button used for submitting a segment
	 */
	handleSubmitSegmentButtonClick() {
		const {
			loadingSelector, confirmSubmitMessage, successMessage, addSuccessMessageAction, openDialogAction, 
			closeDialogAction, collapsable, collapseAfterSubmit, 
		} = this.props;
		const {collapsed} = this.state;

		// Validate segment conditions
		if (!this.validate()) return;
		
		const dialogGUIID = openDialogAction('', ConfirmDialog, {
			message: confirmSubmitMessage ? confirmSubmitMessage : this.t('confirm_submit'),
			onYes: () => {
				this.submitSegment(loadingSelector)
					.then(res => {
						if (res) {
							if (successMessage) addSuccessMessageAction(successMessage);
							
							// Clear conditions
							this.setData([]).then();

							if (collapseAfterSubmit && (collapsable && !collapsed)) this.setState({collapsed: true}).then();
							executeComponentCallback(this.props.onSuccess);
						} else {
							executeComponentCallback(this.props.onError);
						}
					})
					.finally(() => closeDialogAction(dialogGUIID));
			}
		}, {
			id: 'import-from-segment-confirm-dialog',
			closeOnEscape: true,
			closeOnClickOutside: true,
			hideCloseBtn: true,
			maxWidth: 600
		});
		this.setOption(
			'dialogsToCloseOnUnmount',
			trimArray([...this.getOption('dialogsToCloseOnUnmount'), dialogGUIID], 'left')
		);
	}


	// Render methods ---------------------------------------------------------------------------------------------------
	/**
	 * Render the search filter component
	 * @return {JSX.Element}
	 */
	renderSearch() {
		const {scrollParentSelector, readOnly} = this.props;
		const {fieldOptions, fieldOptionsLoading} = this.state;
		/** @type {ConditionListItemDataObject[]} */
		const data = getArray(this.getData())
			.map(
				/** @param {ConditionListItemDataObject} item */
				item => {
					if (!item) return item;
					
					let displayOptions = {};
					
					// Insert relative date tag for days
					if (
						[
							CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.DAY, 
							CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.DATE, 
							CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.DATETIME
						].includes(getString(item, 'field.displayType'))
					) {
						displayOptions = {
							...getObject(item, 'field.displayOptions'),
							showInsertValueButton: true,
							insertValueType: INSERT_VALUE_BUTTON_TYPE.DIALOG,
							insertValueTypeOptions: {
								dialogProps: {
									titleIcon: 'calculator',
									title: this.t('relative_date_dialog_title'),
									sections: [
										new InsertValueDialogSectionDataObject(
											'insert-tag-section',
											RelativeDateSection,
											{
												data: (
													typeof item?.value === 'string' && stringContainsTag(item.value) ? 
														item.value : 
														undefined
												)
											},
											stringContainsTag
										),
									],
								},
								dialogOptions: {
									id: 'insert-tag-dialog',
									className: 'bordered-title',
									closeOnEscape: true,
									closeOnClickOutside: true,
									hideCloseBtn: false,
									maxWidth: 640,
								},
							},
							insertValueButtonProps: {
								icon: 'calculator',
								title: this.t('insert_relative_date'),
							},
						};
						
						if (getString(item, 'field.displayType') === CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.DAY) {
							displayOptions['placeholder'] = '-- --';
						}
					}
					
					return {...item, field: {...item.field, displayOptions}};
				}
			);
		
		return (
			<ConditionList
				readOnly={readOnly}
				customToolbarButtons={[
					{
						label: this.t('browse_dialog_btn'),
						onClick: () => this.openImportSegmentDialog(),
					}
				]}
				enableParenthesis={true}
				selectable={false}
				showLabels={false}
				showBottomToolbar={false}
				scrollParentSelector={scrollParentSelector}
				fieldDisplayType={CONDITION_LIST_FIELD_DISPLAY_TYPE.SELECT}
				fieldDisplayOptions={{
					options: fieldOptions,
					isLoading: fieldOptionsLoading,
				}}
				data={data}
				mapData={false}
				clearValueType={CONDITION_LIST_VALUE_CLEAR_TYPE.ALWAYS}
				onChange={data => {
					this.clearValidationErrors().then();
					
					if (Array.isArray(data) && data.length > 0) {
						let updatedData = cloneDeep(data).map(i => {
							let updated = cloneDeep(i);
							if (getString(i, 'field.displayType') === CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.BOOLEAN) {
								set(updated, 'field.displayOptions.optionsTranslationPath', 'constants.booleans.yes_no');
							}
							return updated;
						});
						return this.setData(updatedData);
					}
					return this.setData(data);
				}}
				errors={this.getValidationErrors()}
				onValidationErrorsChange={errors => 
					isEmpty(errors) ? this.clearValidationErrors() : this.setValidationErrors('', errors)
				}
			/>
		);
	}
	
	/**
	 * Render actions toolbar
	 * @return {JSX.Element}
	 */
	renderActions() {
		const {buttonLabel, buttonProps, showSubmitButton} = this.props;
		const data = getArray(this.getData());

		return (
			<div className="import-actions">
				<Button
					className="preview-segment-btn"
					icon="search-document"
					iconProps={{symbolPrefix: 'icofont-'}}
					label={this.t('preview_btn')}
					displayStyle={BUTTON_STYLE.SUBTLE}
					displayType={BUTTON_DISPLAY_TYPE.TRANSPARENT}
					disabled={data.length === 0}
					onClick={() => {
						const loading = showLoading('.import-actions .preview-segment-btn');
						setTimeout(() => {
							waitingFunction(() => {
								const {updatingData} = this.state;
								if (!updatingData) {
									this.openPreviewDialog();
									return true;
								}
							}, 1, Infinity).then(() => hideLoading(loading));
						});
					}}
				/>
				
				{showSubmitButton ?
					<Button
						className="add-segment-btn"
						icon={icon_font_create_symbol}
						label={buttonLabel ? buttonLabel : this.t('submit_btn')}
						displayStyle={BUTTON_STYLE.HIGHLIGHT}
						{...buttonProps}
						onClick={() => {
							const loading = showLoading('.import-actions .add-segment-btn');
							setTimeout(() => {
								waitingFunction(() => {
									const {updatingData} = this.state;
									if (!updatingData) {
										this.handleSubmitSegmentButtonClick();
										return true;
									}
								}, 1, Infinity).then(() => hideLoading(loading));
							});
						}}
						disabled={data.length === 0}
					/>
					: null
				}
			</div>
		);
	}

	/**
	 * Render the component content
	 * @description This render method is used to render the actual content of the component. This is done with a
	 * separate method to avoid duplicating code because the component can have a collapse wrapper.
	 * @return {JSX.Element}
	 */
	renderContent() {
		return (
			<div id={this.getDomId()} className={`${this.getOption('domPrefix')}`}>
				{this.renderSearch()}
				{this.renderActions()}
			</div>
		);
	}

	render() {
		const {collapsable, collapseWrapperClassName, collapseWrapperTitle} = this.props;
		const {collapsed} = this.state;

		return (
			collapsable ?
				<div className={`from-customer-segments card ${styles['card']} ${getString(collapseWrapperClassName)}`}>
					<div
						className={`card-header with-toolbar no-select ${styles['cardHeader']}`}
						onClick={() => this.setState({collapsed: !collapsed})}
					>
						<Label
							element="span"
							elementProps={{className: 'card-title'}}
							icon="pie-chart"
							content={(typeof collapseWrapperTitle === 'string' ? collapseWrapperTitle : this.t('title'))}
						/>
						<div className="toolbar">
							<Button
								icon={collapsed ? 'caret-down' : 'caret-up'}
								displayStyle={BUTTON_STYLE.SUBTLE}
								displayType={BUTTON_DISPLAY_TYPE.NONE}
							/>
						</div>
					</div>
					<div className={`card-content ${styles['cardContent']} ${collapsed ? 'no-display' : ''}`}>
						{this.renderContent()}
					</div>
				</div>
				:
				this.renderContent()
		);
	}
}

/**
 * Define a component's own props that can be passed to it by parent components
 */
ImportCustomersFromSegment.propTypes = {
	id: PropTypes.string,
	className: PropTypes.string,

	readOnly: PropTypes.bool,
	loadingSelector: PropTypes.string,
	buttonLabel: PropTypes.string,
	buttonProps: PropTypes.object,
	confirmSubmitMessage: PropTypes.string,
	successMessage: PropTypes.string,
	messageDefinitionId: PropTypes.string,
	scrollParentSelector: PropTypes.string,
	collapsable: PropTypes.bool,
	defaultCollapsed: PropTypes.bool,
	collapseAfterSubmit: PropTypes.bool,
	collapseWrapperClassName: PropTypes.string,
	collapseWrapperTitle: PropTypes.string,
	showSubmitButton: PropTypes.bool,
	submitAction: PropTypes.func,
	submitId: PropTypes.string,

	// ID of the project since customers are assigned to projects
	projectId: PropTypes.string.isRequired,
	// @type {ConditionListItemDataObject[]}
	defaultData: PropTypes.arrayOf(PropTypes.object),

	onSuccess: PropTypes.func, // No arguments
	onError: PropTypes.func, // No arguments
};

/**
 * Define component default values for own props
 */
ImportCustomersFromSegment.defaultProps = {
	id: '',
	className: '',
	readOnly: false,
	buttonProps: {},
	collapsable: false,
	defaultCollapsed: true,
	collapseAfterSubmit: true,
	showSubmitButton: true,
};

export default connect(
	mapStateToProps, getGlobalActions(actions), null, {forwardRef: true}
)(ImportCustomersFromSegment);